import React, { useState, useMemo, useEffect } from 'react';
import { Typography, Grid, Card, CardContent, Box, Table, TableHead, TableRow, TableCell, TableBody, GlobalStyles, TextField, Popover, Button, InputAdornment, IconButton } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchDashboardData } from '../../Redux/Reducers/DashboardReducer';
import { useDispatch } from 'react-redux';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

function Dashboard() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth || state.AuthReducer || {});
    const getDefaultDateRange = () => {
        const today = new Date();

        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        const end = today;
        const format = (date) => {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const d = String(date.getDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
        };

        return {
            startDate: format(start),
            endDate: format(end),
        };
    };

    const defaultRange = getDefaultDateRange();

    const [chartDateRanges, setChartDateRanges] = useState({
        listeningTime: defaultRange,
        userGrowth: defaultRange,
        conversion: defaultRange,
    });

    const {
        data: reduxData = {},
        storageUsage: reduxStorageUsage = {},
        listeningTimeGraph: reduxListeningTimeGraph = [],
        userGrowthGraph: reduxUserGrowthGraph = [],
        topTracks: reduxTopTracks = [],
        listeningPerUser: reduxListeningPerUser = [],
    } = useSelector((state) => state.dashboard || {});

    const [dashboardState, setDashboardState] = useState({
        data: {},
        storageUsage: {},
        listeningTimeGraph: [],
        userGrowthGraph: [],
        topTracks: [],
        listeningPerUser: [],
    });
    const [activeChartKey, setActiveChartKey] = useState(null);
    const [dateRangeAnchorEl, setDateRangeAnchorEl] = useState(null);
    const [draftDateRange, setDraftDateRange] = useState({ startDate: '', endDate: '' });
    const [calendarStartMonth, setCalendarStartMonth] = useState(() => {
        const today = new Date();
        return new Date(today.getFullYear(), today.getMonth(), 1);
    });

    const { data, storageUsage, listeningTimeGraph, userGrowthGraph, conversionRateGraph } = dashboardState;

    useEffect(() => {
        const nextDashboardState = {
            data: reduxData,
            storageUsage: reduxStorageUsage,
            listeningTimeGraph: reduxListeningTimeGraph,
            userGrowthGraph: reduxUserGrowthGraph,
            topTracks: reduxTopTracks,
            listeningPerUser: reduxListeningPerUser,
        };

        setDashboardState(nextDashboardState);
    }, [reduxData, reduxStorageUsage, reduxListeningTimeGraph, reduxUserGrowthGraph, reduxTopTracks, reduxListeningPerUser]);
    // format date for chart display (e.g., Jan 12)
    const formatGraphDate = (dateValue) => {
        if (!dateValue) return '';
        const date = new Date(dateValue);

        if (Number.isNaN(date.getTime())) return dateValue;

        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
        });
    };
    // extract numeric value safely from nested API response
    const getNestedNumber = (source, paths) => {
        for (const path of paths) {
            const value = path.split('.').reduce((result, key) => result?.[key], source);

            if (typeof value === 'number') return value;
            if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
                return Number(value);
            }
        }

        const groupedValues = [source?._sum, source?._count, source?._avg].filter(Boolean);
        for (const group of groupedValues) {
            const numericValue = Object.values(group).find((value) => typeof value === 'number');
            if (typeof numericValue === 'number') return numericValue;
        }

        return 0;
    };
    // normalize chart data into consistent format for charts
    const normalizeChartData = (items) => {
        const normalizedItems = Array.isArray(items)
            ? items
                .map((item, index) => {
                    const numericValue = Number(item?.value);

                    return {
                        dateLabel: item?.dateLabel || `${index + 1}`,
                        value: Number.isFinite(numericValue) ? numericValue : 0,
                    };
                })
                .filter((item) => item.dateLabel)
            : [];

        return normalizedItems.length ? normalizedItems : [{ dateLabel: '-', value: 0 }];
    };
    // prepare listening time chart data 
    const listeningTimeChartData = useMemo(() => (
        normalizeChartData(
            Array.isArray(listeningTimeGraph)
                ? listeningTimeGraph.map((item) => ({
                    dateLabel: formatGraphDate(item.date || item.createdAt || item.day),
                    value: getNestedNumber(item, [
                        '_sum.totalListenSec',
                        '_sum.totalListenMin',
                        'totalListenSec',
                        'total_listen_sec',
                        'listenSeconds',
                        'value',
                    ]),
                }))
                : []
        )
    ), [listeningTimeGraph]);
    // prepare user growth chart data 

    const userGrowthChartData = useMemo(() => (
        normalizeChartData(
            Array.isArray(userGrowthGraph)
                ? userGrowthGraph.map((item) => ({
                    dateLabel: formatGraphDate(item.date || item.createdAt || item.day),
                    value: getNestedNumber(item, [
                        '_count.id',
                        '_count.user_id',
                        '_count.users',
                        'newUsers',
                        'totalUsers',
                        'count',
                        'value',
                    ]),
                }))
                : []
        )
    ), [userGrowthGraph]);
    // prepare conversion rate chart data 
    const conversionRateChartData = useMemo(() => (
        normalizeChartData(
            Array.isArray(conversionRateGraph)
                ? conversionRateGraph.map((item) => ({
                    dateLabel: formatGraphDate(item.date || item.createdAt || item.day),
                    value: getNestedNumber(item, [
                        'conversionRate',
                        'conversion_rate',
                        'rate',
                        'percentage',
                        'value',
                    ]),
                }))
                : []
        )
    ), [conversionRateGraph]);

    const getTrackDisplayTitle = (item) => (
        item?.track_title || item?.track_title || item?.track_title || ''
    );

    const getTrackDurationMinutes = (seconds) => {
        const totalSeconds = Number(seconds);

        if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
            return '0 min';
        }

        const mins = Math.floor(totalSeconds / 60);
        const secs = Math.round(totalSeconds % 60);

        if (mins === 0) {
            return `${secs} sec`;
        }
        if (secs === 0) {
            return `${mins} min`;
        }

        return `${mins} min ${secs} sec`;
    };

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

    const formatDateForApi = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const parseApiDate = (dateValue) => {
        if (!dateValue) return null;
        const [year, month, day] = dateValue.split('-').map(Number);
        return new Date(year, month - 1, day);
    };

    const isSameDay = (firstDate, secondDate) => (
        firstDate && secondDate && formatDateForApi(firstDate) === formatDateForApi(secondDate)
    );

    const isDateInRange = (date, startDate, endDate) => (
        startDate && endDate && date > startDate && date < endDate
    );

    // const isToday = (date) => {
    //     const today = new Date();
    //     return (
    //         date.getDate() === today.getDate() &&
    //         date.getMonth() === today.getMonth() &&
    //         date.getFullYear() === today.getFullYear()
    //     );
    // };

    const getCalendarDays = (monthDate) => {
        const year = monthDate.getFullYear();
        const month = monthDate.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const totalDays = new Date(year, month + 1, 0).getDate();
        const days = Array.from({ length: firstDay }, () => null);

        for (let day = 1; day <= totalDays; day += 1) {
            days.push(new Date(year, month, day));
        }

        return days;
    };

    const shiftCalendarMonth = (amount) => {
        setCalendarStartMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + amount, 1));
    };

    const getActiveDateRange = () => (
        activeChartKey ? draftDateRange : { startDate: '', endDate: '' }
    );

    const handleCalendarDateClick = (date) => {
        if (!activeChartKey) return;
        const today = new Date();
        const clickedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const normalizedToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());

        if (clickedDate > normalizedToday) return;

        const selectedStartDate = parseApiDate(draftDateRange.startDate);
        const selectedEndDate = parseApiDate(draftDateRange.endDate);

        // First click selects start, second click selects end. Third click restarts selection.
        if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
            setDraftDateRange({
                startDate: formatDateForApi(clickedDate),
                endDate: '',
            });
            return;
        }

        if (clickedDate < selectedStartDate) {
            setDraftDateRange({
                startDate: formatDateForApi(clickedDate),
                endDate: formatDateForApi(selectedStartDate),
            });
            return;
        }

        setDraftDateRange((prev) => ({
            ...prev,
            endDate: formatDateForApi(clickedDate),
        }));
    };
    const formatDisplayDate = (dateValue) => {
        if (!dateValue) return '';

        const [year, month, day] = dateValue.split('-');

        return `${day}-${month}-${year}`;
    };

    useEffect(() => {
        dispatch(fetchDashboardData());
    }, [dispatch]);

    const handleApply = () => {
        if (!activeChartKey) return;
        const formatForRequest = (dateValue) => {
            if (!dateValue) return '';
            const [year, month, day] = dateValue.split('-');
            return `${year}-${day}-${month}`;
        };

        setChartDateRanges((prev) => ({
            ...prev,
            [activeChartKey]: draftDateRange,
        }));

        const params = {
            ...(draftDateRange.startDate ? { start_date: formatForRequest(draftDateRange.startDate) } : {}),
            ...(draftDateRange.endDate ? { end_date: formatForRequest(draftDateRange.endDate) } : {}),
        };

        dispatch(
            fetchDashboardData({
                [activeChartKey]: params,
            })
        );

        closeDateRangePicker();
    };

    const dateRangeFieldSx = {
        width: { xs: '100%', sm: 300 },
        '& .MuiInputBase-root': {
            height: 42,
            color: '#fff',
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            borderRadius: '6px',
            cursor: 'pointer',
        },
        '& .MuiInputBase-input': {
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600,
            textAlign: 'center',
            '&::placeholder': {
                color: '#fff',
                opacity: 1,
            },
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(230, 210, 249, 0.42)',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FF69B4',
        },
        '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FF69B4 !important',
        },
        '& .MuiSvgIcon-root': {
            color: '#FF69B4',
            fontSize: '18px',
        },
    };

    const openDateRangePicker = (event, chartKey) => {
        const range = chartDateRanges[chartKey];
        const selectedStartDate = parseApiDate(range.startDate);

        if (selectedStartDate) {
            setCalendarStartMonth(new Date(selectedStartDate.getFullYear(), selectedStartDate.getMonth(), 1));
        }

        setActiveChartKey(chartKey);
        setDraftDateRange(range);
        setDateRangeAnchorEl(event.currentTarget);
    };

    const closeDateRangePicker = () => {
        setDateRangeAnchorEl(null);
        setActiveChartKey(null);
    };

    const renderDateRangePicker = (chartKey) => {
        const range = chartDateRanges[chartKey];
        const dateRangeDisplay = range.startDate || range.endDate
            ? `${formatDisplayDate(range.startDate) || 'MM/DD/YYYY'} - ${formatDisplayDate(range.endDate) || 'MM/DD/YYYY'}`
            : '';

        return (
            <TextField
                value={dateRangeDisplay}
                placeholder="MM/DD/YYYY - MM/DD/YYYY"
                size="small"
                onClick={(event) => openDateRangePicker(event, chartKey)}
                inputProps={{ readOnly: true }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <CalendarTodayIcon />
                        </InputAdornment>
                    ),
                }}
                sx={dateRangeFieldSx}
            />
        );
    };

    const renderMonthCalendar = (monthDate, showPrevArrow = false, showNextArrow = false) => {
        const activeRange = getActiveDateRange();
        const selectedStartDate = parseApiDate(activeRange.startDate);
        const selectedEndDate = parseApiDate(activeRange.endDate);
        const today = new Date();
        const normalizedToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const isFutureMonth = monthDate.getFullYear() > normalizedToday.getFullYear()
            || (monthDate.getFullYear() === normalizedToday.getFullYear() && monthDate.getMonth() >= normalizedToday.getMonth());

        return (
            <Box sx={{ width: 280, px: '16px', py: '16px' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '32px 1fr 32px', alignItems: 'center', mb: '18px' }}>
                    <Box>
                        {showPrevArrow && (
                            <IconButton size="small" onClick={() => shiftCalendarMonth(-1)} sx={{ color: '#777', width: 30, height: 30 }}>
                                <ChevronLeftIcon />
                            </IconButton>
                        )}
                    </Box>
                    <Typography sx={{ color: '#222', fontSize: '16px', textAlign: 'center', fontWeight: 500 }}>
                        {monthNames[monthDate.getMonth()]} {monthDate.getFullYear()}
                    </Typography>
                    <Box>
                        {showNextArrow && (
                            <IconButton
                                size="small"
                                onClick={() => shiftCalendarMonth(1)}
                                disabled={isFutureMonth}
                                sx={{ color: '#777', width: 30, height: 30 }}
                            >
                                <ChevronRightIcon />
                            </IconButton>
                        )}
                    </Box>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 40px)', rowGap: '4px' }}>
                    {weekDays.map((day, index) => (
                        <Typography key={`${day}-${index}`} sx={{ color: '#777', fontSize: '13px', textAlign: 'center', lineHeight: '32px', mb: '4px' }}>
                            {day}
                        </Typography>
                    ))}
                    {getCalendarDays(monthDate).map((date, index) => {
                        if (!date) {
                            return <Box key={`empty-${index}`} sx={{ width: 40, height: 32 }} />;
                        }

                        const isSelectedStart = isSameDay(date, selectedStartDate);
                        const isSelectedEnd = isSameDay(date, selectedEndDate);
                        const isBetween = isDateInRange(date, selectedStartDate, selectedEndDate);
                        const isFutureDate = date > normalizedToday;
                        const hasCompleteRange = Boolean(selectedStartDate && selectedEndDate);
                        const isRangeDate = hasCompleteRange && (isSelectedStart || isSelectedEnd || isBetween);

                        return (
                            <Box
                                key={formatDateForApi(date)}
                                onClick={() => !isFutureDate && handleCalendarDateClick(date)}
                                sx={{
                                    width: 40,
                                    height: 32,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: isFutureDate ? 'not-allowed' : 'pointer',
                                    opacity: isFutureDate ? 0.35 : 1,
                                    backgroundColor: isRangeDate ? '#1a1a1a' : 'transparent',
                                    // backgroundColor: isRangeDate ? 'rgba(255, 255, 255, 0.14)' : 'transparent',
                                    borderTopLeftRadius: isSelectedStart ? '18px' : 0,
                                    borderBottomLeftRadius: isSelectedStart ? '18px' : 0,
                                    borderTopRightRadius: isSelectedEnd ? '18px' : 0,
                                    borderBottomRightRadius: isSelectedEnd ? '18px' : 0,
                                    '&:hover .date-day': {
                                        borderColor: '#8aa0b8',
                                        backgroundColor: '#eef5ff',
                                        // borderColor: isFutureDate ? 'transparent' : '#b8c8da',
                                        // backgroundColor: isFutureDate ? 'transparent' : 'rgba(255, 255, 255, 0.1)',
                                    },
                                }}
                            >
                                <Box
                                    className="date-day"
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#fff',
                                        fontSize: '13px',
                                        border: isSelectedStart || isSelectedEnd ? '1px solid #d6e2f0' : '1px solid transparent',
                                        backgroundColor: isSelectedStart || isSelectedEnd ? '#1a1a1a' : 'transparent',
                                        position: 'relative',
                                        zIndex: 1,
                                    }}
                                >
                                    {date.getDate()}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        );
    };

    const ChartHeader = ({ title, chartKey }) => (
        <Box
            sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                gap: '16px',
                mb: '20px',
                flexDirection: { xs: 'column', sm: 'row' },
            }}
        >
            <Typography variant="h5" sx={{ fontSize: '18px', color: "#fff", textTransform: 'capitalize', fontWeight: '500' }}>
                {title}
            </Typography>
            {renderDateRangePicker(chartKey)}
        </Box>
    );

    return (
        <>
            <Box sx={{ p: '36px 24px 24px' }}>
                <Box sx={{ mb: '10px', }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', lineHeight: '32px' }} className='main_title'>
                        {t('dashboard_overview', 'Dashboard Overview')}
                    </Typography>
                </Box>

                {/* ===== NEW SECTION: User / Podcaster / Music / Host ===== */}
                <Box>
                    {/* <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                    {t('content_overview', 'Content Overview')}
                </Typography> */}

                    <Grid container spacing={3} sx={{ marginTop: '0' }}>
                        {/* Users */}
                        {(user.roleName !== "Podcaster" || user.roleId !== 4) && (
                            <Grid item xs={12} sm={6} lg={4} xl={3} style={{ paddingTop: '24px' }}>
                                <Card sx={{ borderRadius: '10px', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 10px 20px rgba(255, 105, 180, 0.2)' } }}>
                                    <CardContent sx={{ textAlign: 'center', p: '20px !important' }}>
                                        <Box sx={{ width: '65px', height: '65px', margin: '0 auto', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(230, 210, 249, 0.2)', border: '1px solid rgba(230, 210, 249, 0.1)', }}>
                                            {/* <i className="icofont-users" style={{ fontSize: 30, color: '#FF69B4' }} /> */}
                                            <img src="/assets/images/active_user.svg" alt='Active Users' />
                                        </Box>
                                        <Typography variant="h5" sx={{ mt: '12px', mb: '6px', color: '#fff', fontWeight: '600' }}>
                                            {data?.total_users || 0}
                                        </Typography>
                                        <Typography sx={{ color: '#B0B0B0', textTransform: 'capitalize' }}>
                                            {t('dashboard_page.total_active_users', 'Total Active Users')}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}

                        {(user.roleName !== "Podcaster" || user.roleId !== 4) && (
                            <Grid item xs={12} sm={6} lg={4} xl={3} style={{ paddingTop: '24px' }}>
                                <Card sx={{ borderRadius: '10px', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 10px 20px rgba(255, 105, 180, 0.2)' } }}>
                                    <CardContent sx={{ textAlign: 'center', p: '20px !important' }}>
                                        <Box sx={{ width: '65px', height: '65px', margin: '0 auto', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(230, 210, 249, 0.2)', border: '1px solid rgba(230, 210, 249, 0.1)', }}>
                                            {/* <i className="icofont-users" style={{ fontSize: 30, color: '#FF69B4' }} /> */}
                                            <img src="/assets/images/inactive_user.svg" alt='Inactive Users' />
                                        </Box>
                                        <Typography variant="h5" sx={{ mt: '12px', mb: '6px', color: '#fff', fontWeight: '600' }}>
                                            {data?.total_inactive_users || 0}
                                        </Typography>
                                        <Typography sx={{ color: '#B0B0B0', textTransform: 'capitalize' }}>
                                            {t('dashboard_page.total_inactive_users', 'Total Inactive Users')}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}

                        {/* Music */}
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            lg={4}
                            xl={3}
                            style={{ paddingTop: '24px' }}
                        >
                            <Card
                                sx={{
                                    borderRadius: '10px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    border: '1px solid rgba(230, 210, 249, 0.2)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-5px)',
                                        boxShadow: '0 10px 20px rgba(255, 105, 180, 0.2)',
                                    },
                                }}
                            >
                                <CardContent
                                    sx={{
                                        textAlign: 'center',
                                        p: '20px !important',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: '65px',
                                            height: '65px',
                                            margin: '0 auto',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: 'rgba(230, 210, 249, 0.2)',
                                            border: '1px solid rgba(230, 210, 249, 0.1)',
                                        }}
                                    >
                                        <img
                                            src="/assets/images/listening_time.svg"
                                            alt="Listening Time"
                                        />
                                    </Box>

                                    <Typography
                                        variant="h5"
                                        sx={{
                                            mt: '12px',
                                            mb: '6px',
                                            color: '#fff',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {Math.floor((data?.total_listening_time || 0) / 60)}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            color: '#B0B0B0',
                                            textTransform: 'capitalize',
                                        }}
                                    >
                                        {t(
                                            'dashboard_page.total_listening_time',
                                            'Total listening time'
                                        )}(Min)
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Podcasters */}
                        {(user.roleName !== "Podcaster" || user.roleId !== 4) && (
                            <Grid item xs={12} sm={6} lg={4} xl={3} style={{ paddingTop: '24px' }}>
                                <Card sx={{ borderRadius: '10px', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 10px 20px rgba(255, 105, 180, 0.2)' } }}>
                                    <CardContent sx={{ textAlign: 'center', p: '20px !important' }}>
                                        <Box sx={{ width: '65px', height: '65px', margin: '0 auto', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(230, 210, 249, 0.2)', border: '1px solid rgba(230, 210, 249, 0.1)', }}>
                                            {/* <i className="icofont-radio-mic" style={{ fontSize: 30, color: '#FF69B4' }} /> */}
                                            <img src="/assets/images/podcasters.svg" alt='Podcasters' />
                                        </Box>
                                        <Typography variant="h5" sx={{ mt: '12px', mb: '6px', color: '#fff', fontWeight: '600' }}>
                                            {data?.total_podcasters || 0}
                                        </Typography>
                                        <Typography sx={{ color: '#B0B0B0', textTransform: 'capitalize' }}>
                                            {t('dashboard_page.total_podcasters', 'Number of podcasters')}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}

                        {/* Host / Heer */}
                        <Grid item xs={12} sm={6} lg={4} xl={3} style={{ paddingTop: '24px' }}>
                            <Card sx={{ borderRadius: '10px', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 10px 20px rgba(255, 105, 180, 0.2)' } }}>
                                <CardContent sx={{ textAlign: 'center', p: '20px !important' }}>
                                    <Box sx={{ width: '65px', height: '65px', margin: '0 auto', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(230, 210, 249, 0.2)', border: '1px solid rgba(230, 210, 249, 0.1)', }}>
                                        {/* <i className="icofont-microphone" style={{ fontSize: 30, color: '#FF69B4' }} /> */}
                                        <img src="/assets/images/content_podcasts.svg" alt='Podcasters' />
                                    </Box>
                                    <Typography variant="h5" sx={{ mt: '12px', mb: '6px', color: '#fff', fontWeight: '600' }}>{data?.total_podcasts || 0}</Typography>
                                    <Typography sx={{ color: '#B0B0B0', textTransform: 'capitalize' }}>
                                        {t('dashboard_page.total_content_podcasts', 'Total Content Podcasts')}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* <Grid item xs={12} sm={6} lg={4} xl={3} style={{ paddingTop: '24px' }}>
                            <Card sx={{ borderRadius: '10px', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 10px 20px rgba(255, 105, 180, 0.2)' } }}>
                                <CardContent sx={{ textAlign: 'center', p: '20px !important' }}>
                                    <Box sx={{ width: '65px', height: '65px', margin: '0 auto', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(230, 210, 249, 0.2)', border: '1px solid rgba(230, 210, 249, 0.1)', }}>
                                        {/* <i className="icofont-microphone" style={{ fontSize: 30, color: '#FF69B4' }} /> */}
                        {/* <img src="/assets/images/content_files.svg" alt='Files' />
                                    </Box>
                                    <Typography variant="h5" sx={{ mt: '12px', mb: '6px', color: '#fff', fontWeight: '600' }}>{data.counts?.total_podcasts || 0}</Typography>
                                    <Typography sx={{ color: '#B0B0B0', textTransform: 'capitalize' }}>
                                        {t('dashboard_page.total_content_files', 'Total Content Files')}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid> */}

                        {/* Storage usage */}
                        <Grid item xs={12} sm={6} lg={4} xl={3} style={{ paddingTop: '24px' }}>
                            <Card sx={{ borderRadius: '10px', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 10px 20px rgba(255, 105, 180, 0.2)' } }}>
                                <CardContent sx={{ textAlign: 'center', p: '20px !important' }}>
                                    <Box sx={{ width: '65px', height: '65px', margin: '0 auto', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(230, 210, 249, 0.2)', border: '1px solid rgba(230, 210, 249, 0.1)', }}>
                                        {/* <i className="icofont-briefcase" style={{ fontSize: 30, color: '#FF69B4' }} /> */}
                                        <img src="/assets/images/storage_usage.svg" alt='Storage' />
                                    </Box>
                                    <Typography variant="h5" sx={{ mt: '12px', mb: '6px', color: '#fff', fontWeight: '600' }}>{storageUsage?.summary?.totalBandwidth?.gb || 0}</Typography>
                                    <Typography sx={{ color: '#B0B0B0', textTransform: 'capitalize' }}>
                                        {t('dashboard_page.total_storage_usage', 'Storage usage')}(GB)
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Most listened-to podcasts */}
                        {/* <Grid item xs={12} sm={6} lg={4} xl={3} style={{ paddingTop: '24px' }}>
                        <Card sx={{ borderRadius: '10px', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)', transition: 'all 0.3s ease', '&:hover': {transform: 'translateY(-5px)', boxShadow: '0 10px 20px rgba(255, 105, 180, 0.2)'} }}>
                            <CardContent sx={{ textAlign: 'center', p: '20px !important' }}>
                                <Box sx={{ width: '65px', height: '65px', margin: '0 auto', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',backgroundColor: 'rgba(230, 210, 249, 0.2)', border: '1px solid rgba(230, 210, 249, 0.1)', }}>
                                    <i className="icofont-briefcase" style={{ fontSize: 30, color: '#FF69B4' }} />
                                </Box>
                                <Typography variant="h5" sx={{ mt: '12px', mb: '6px', color: '#fff', fontWeight: '600' }}>{ data.counts?.most_listened_podcasts || 0 }</Typography>
                                <Typography sx={{ color: '#B0B0B0' }}>
                                    {t('dashboard_page.most_listened_podcasts', 'Most listened-to podcasts')}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid> */}

                        {/* Conversion rate from the free version to the premium version */}
                        {(user.roleName !== "Podcaster" || user.roleId !== 4) && (
                            <Grid item xs={12} sm={6} lg={4} xl={3} style={{ paddingTop: '24px' }}>
                                <Card sx={{ borderRadius: '10px', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 10px 20px rgba(255, 105, 180, 0.2)' } }}>
                                    <CardContent sx={{ textAlign: 'center', p: '20px !important' }}>
                                        <Box sx={{ width: '65px', height: '65px', margin: '0 auto', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(230, 210, 249, 0.2)', border: '1px solid rgba(230, 210, 249, 0.1)', }}>
                                            {/* <i className="icofont-briefcase" style={{ fontSize: 30, color: '#FF69B4' }} /> */}
                                            <img src="/assets/images/conversion_rate.svg" alt='Conversion Rate' />
                                        </Box>
                                        <Typography variant="h5" sx={{ mt: '12px', mb: '6px', color: '#fff', fontWeight: '600' }}>{data?.conversion_rate || 0}</Typography>
                                        <Typography sx={{ color: '#B0B0B0', textTransform: 'capitalize' }}>
                                            {t(
                                                'dashboard_page.conversion_rate_free_to_premium',
                                                'Conversion Rate (Free → Premium)'
                                            )}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                </Box>

                {/* Charts Area */}
                {(user.roleName !== "Podcaster" || user.roleId !== 4) && (
                    <Box sx={{ mt: '30px' }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Card
                                    sx={{
                                        borderRadius: '10px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        border: '1px solid rgba(230, 210, 249, 0.2)',
                                        p: '20px',
                                        '& .MuiFormControl-root': {
                                            width: '270px'
                                        },
                                        '& .MuiFormControl-root .MuiInputBase-root .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(230, 210, 249, 0.1)'
                                        }
                                    }}>
                                    {/* <ChartHeader title="Listening over time" chartKey="listeningTime" /> */}
                                    <ChartHeader
                                        title={t('dashboard_page.listening_over_time', 'Listening over time')}
                                        chartKey="listeningTime"
                                    />
                                    <BarChart
                                        dataset={listeningTimeChartData}
                                        xAxis={[
                                            {
                                                dataKey: 'dateLabel',
                                                scaleType: 'band',
                                                tickLabelStyle: { fill: '#fff' }
                                            },
                                        ]}
                                        yAxis={[
                                            {
                                                tickLabelStyle: { fill: '#fff' },
                                                valueFormatter: (value) => Number(value).toLocaleString(),
                                            },
                                        ]}
                                        series={[
                                            {
                                                dataKey: 'value',
                                                // label: 'Seoul rainfall',
                                                color: '#FF69B4',
                                            },
                                        ]}
                                        height={400}
                                        sx={{
                                            '& .MuiChartsAxis-line': {
                                                stroke: '#e6d2f933 !important',
                                            },
                                            '& .MuiChartsAxis-tick': {
                                                stroke: '#e6d2f933 !important',
                                            },
                                        }}
                                    />
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Card
                                    sx={{
                                        borderRadius: '10px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        border: '1px solid rgba(230, 210, 249, 0.2)',
                                        p: '20px',
                                        '& .MuiFormControl-root': {
                                            width: '270px'
                                        },
                                        '& .MuiFormControl-root .MuiInputBase-root .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(230, 210, 249, 0.1)'
                                        }
                                    }}>
                                    {/* <ChartHeader title="New users over time" chartKey="userGrowth" /> */}
                                    <ChartHeader
                                        title={t('dashboard_page.new_users_over_time', 'New users over time')}
                                        chartKey="userGrowth"
                                    />
                                    <BarChart
                                        dataset={userGrowthChartData}
                                        xAxis={[
                                            {
                                                dataKey: 'dateLabel',
                                                scaleType: 'band',
                                                tickLabelStyle: { fill: '#fff' }
                                            },
                                        ]}
                                        yAxis={[
                                            {
                                                tickLabelStyle: { fill: '#fff' },
                                                valueFormatter: (value) => Number(value).toLocaleString(),
                                            },
                                        ]}
                                        series={[
                                            {
                                                dataKey: 'value',
                                                // label: 'Seoul rainfall',
                                                color: '#FF69B4',
                                            },
                                        ]}
                                        height={400}
                                        sx={{
                                            '& .MuiChartsAxis-line': {
                                                stroke: '#e6d2f933 !important',
                                            },
                                            '& .MuiChartsAxis-tick': {
                                                stroke: '#e6d2f933 !important',
                                            },
                                        }}
                                    />
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <Card
                                    sx={{
                                        borderRadius: '10px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        border: '1px solid rgba(230, 210, 249, 0.2)',
                                        p: '20px',
                                        '& .MuiFormControl-root': {
                                            width: '270px'
                                        },
                                        '& .MuiFormControl-root .MuiInputBase-root .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(230, 210, 249, 0.1)'
                                        }
                                    }}
                                >
                                    {/* <ChartHeader title="Conversion rate over time" chartKey="conversion" /> */}
                                    <ChartHeader
                                        title={t('dashboard_page.conversion_rate_over_time', 'Conversion rate over time')}
                                        chartKey="conversion"
                                    />
                                    <LineChart
                                        xAxis={[
                                            {
                                                data: conversionRateChartData.map((item) => item.dateLabel),
                                                scaleType: 'point',
                                                tickLabelStyle: { fill: '#fff' },
                                            },
                                        ]}
                                        yAxis={[
                                            {
                                                tickLabelStyle: { fill: '#fff' },
                                            },
                                        ]}
                                        series={[
                                            {
                                                data: conversionRateChartData.map((item) => item.value),
                                                color: '#FF69B4',
                                                showMark: ({ index }) => index % 2 === 0,
                                            },
                                        ]}
                                        height={400}
                                        slotProps={{
                                            tooltip: {
                                                sx: {
                                                    backgroundColor: '#1e1e1e',
                                                    color: '#fff',
                                                    border: '1px solid rgba(255,255,255,0.2)',
                                                    borderRadius: '8px',
                                                },
                                            },
                                        }}
                                        sx={{
                                            '& .MuiChartsAxis-line': {
                                                stroke: '#e6d2f933',
                                            },
                                            '& .MuiChartsAxis-tick': {
                                                stroke: '#e6d2f933',
                                            },
                                            '& .MuiChartsAxis-tickLabel': {
                                                fill: '#fff',
                                            },
                                            '& .MuiChartsAxis-label': {
                                                fill: '#fff',
                                            },
                                            '& .MuiChartsGrid-line': {
                                                stroke: 'rgba(255,255,255,1)',
                                            },
                                        }}
                                    />
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* Most Listened To Podcasts Table */}
                <Box sx={{ mt: '30px' }}>
                    <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)', borderRadius: '10px', overflow: 'hidden', p: '16px' }}>
                        <CardContent sx={{ padding: '0 !important' }}>
                            <Typography variant="h5" gutterBottom sx={{ color: 'white', fontSize: "18px", mb: "10px", fontWeight: '500', textTransform: 'capitalize' }}>{t('dashboard_page.most_listened_podcasts', 'Most listened podcasts')}</Typography>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                color: '#fff',
                                                fontSize: '14px',
                                                p: '14px 10px',
                                                borderBottomColor: "rgba(230, 210, 249, 0.2)"
                                            }}
                                        >
                                            {t('dashboard_page.album_name', 'Album Name')}
                                        </TableCell>

                                        <TableCell
                                            sx={{
                                                color: '#fff',
                                                fontSize: '14px',
                                                p: '14px 10px',
                                                borderBottomColor: "rgba(230, 210, 249, 0.2)"
                                            }}
                                        >
                                            {t('dashboard_page.episode_name', 'Episode Name')}
                                        </TableCell>

                                        <TableCell
                                            sx={{
                                                color: '#fff',
                                                fontSize: '14px',
                                                p: '14px 10px',
                                                borderBottomColor: "rgba(230, 210, 249, 0.2)"
                                            }}
                                        >
                                            {t('dashboard_page.episode_image', 'Episode Image')}
                                        </TableCell>

                                        <TableCell
                                            sx={{
                                                color: '#fff',
                                                fontSize: '14px',
                                                p: '14px 10px',
                                                borderBottomColor: "rgba(230, 210, 249, 0.2)"
                                            }}
                                        >
                                            {t('dashboard_page.listening_time', 'Listening Time')}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reduxTopTracks?.length > 0 ? (
                                        reduxTopTracks?.map((item, index) => (
                                            <TableRow key={item.track_id || index}>
                                                {/* Album Name */}
                                                <TableCell sx={{ color: '#fff', fontSize: '14px', p: '14px 10px' }}>
                                                    {item.album_title || '-'}
                                                </TableCell>

                                                {/* Episode Name */}
                                                <TableCell sx={{ color: '#fff', fontSize: '14px', p: '14px 10px' }}>
                                                    {getTrackDisplayTitle(item)}
                                                </TableCell>

                                                {/* Episode Image */}
                                                <TableCell sx={{ p: '14px 10px' }}>
                                                    {item.cover_image ? (
                                                        <img
                                                            src={item.cover_image}
                                                            alt={getTrackDisplayTitle(item)}
                                                            style={{
                                                                width: 50,
                                                                height: 50,
                                                                borderRadius: 6,
                                                                objectFit: 'cover'
                                                            }}
                                                        />
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                width: 50,
                                                                height: 50,
                                                                borderRadius: 1.5,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                color: '#aaa',
                                                                fontSize: '12px',
                                                                background: 'rgba(255,255,255,0.06)',
                                                            }}
                                                        >
                                                            N/A
                                                        </Box>
                                                    )}
                                                </TableCell>

                                                {/* Episode Time */}
                                                <TableCell sx={{ color: '#fff', fontSize: '14px', p: '14px 10px' }}>
                                                    {getTrackDurationMinutes(item.total_listen_sec)}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ color: '#aaa', borderColor: 'rgba(230, 210, 249, 0.2)' }}>
                                                {t('dashboard_page.no_data_found', 'No Data Found')}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            <GlobalStyles
                styles={{
                    '.MuiChartsTooltip-root': {
                        backgroundColor: '#1a1a1a !important',
                        border: '1px solid #433f47 !important',
                        color: '#fff !important',
                    },
                    '.MuiChartsTooltip-root th': {
                        color: '#fff !important',
                        fontWeight: '600',
                        borderBottom: '1px solid #433f47 !important',
                    },
                    '.MuiChartsTooltip-root td': {
                        color: '#fff !important',
                        borderBottom: '1px solid #433f47 !important',
                    },
                    '.MuiChartsTooltip-valueCell': {
                        color: '#fff !important',
                    },
                    '.MuiChartsTooltip-labelCell': {
                        color: '#aaa !important',
                    },
                }}
            />
            <Popover
                open={Boolean(dateRangeAnchorEl)}
                onClose={closeDateRangePicker}
                anchorReference="anchorPosition"
                anchorPosition={{
                    top: window.innerHeight / 2 - 50,
                    left: window.innerWidth / 2,
                }}

                transformOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                }}
                slotProps={{
                    popper: {
                        modifiers: [
                            {
                                name: 'flip',
                                enabled: false,
                            },
                            {
                                name: 'preventOverflow',
                                enabled: false,
                            },
                        ],
                    },
                }}
                PaperProps={{
                    sx: {
                        mb: '8px',
                        backgroundColor: '#252525',
                        border: '1px solid rgba(230, 210, 249, 0.1)',
                        borderRadius: '20px',
                        overflow: 'hidden',
                        maxWidth: 'calc(100vw - 24px)',
                        '& .MuiTypography-root': {
                            color: '#fff'
                        },
                        '& > .MuiBox-root:first-child>.MuiBox-root>.MuiBox-root:last-child': {
                            gridTemplateColumns: 'repeat(7, 35px)'
                        },
                        '& .date-day': {
                            color: '#fff',
                        },
                        '& > .MuiBox-root:first-child>.MuiBox-root>.MuiBox-root:last-child>.MuiBox-root.css-d3li3k, & > .MuiBox-root:first-child>.MuiBox-root>.MuiBox-root:last-child>.MuiBox-root.css-ojo3b, & > .MuiBox-root:first-child>.MuiBox-root>.MuiBox-root:last-child>.MuiBox-root.css-efuikm, & > .MuiBox-root:first-child>.MuiBox-root>.MuiBox-root:last-child>.MuiBox-root.css-wm6xr4, & > .MuiBox-root:first-child>.MuiBox-root>.MuiBox-root:last-child>.MuiBox-root .css-prrdws': {
                            backgroundColor: '#1a1a1a'
                        },
                        '& > .MuiBox-root:first-child>.MuiBox-root>.MuiBox-root:last-child>.MuiBox-root:hover>.date-day': {
                            backgroundColor: '#252525',
                        },
                        '& > .MuiBox-root:first-child>.MuiBox-root:nth-child(2)': {
                            backgroundColor: 'rgba(230, 210, 249, 0.1)'
                        },
                        '& > .MuiBox-root:last-child': {
                            borderTop: '1px solid rgba(230, 210, 249, 0.1)',
                            paddingTop: '12px'
                        }
                    },
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                    {renderMonthCalendar(calendarStartMonth, true, false)}
                    <Box sx={{ width: { xs: '100%', md: '1px' }, height: { xs: '1px', md: 'auto' }, backgroundColor: '#e1e1e1' }} />
                    {renderMonthCalendar(new Date(calendarStartMonth.getFullYear(), calendarStartMonth.getMonth() + 1, 1), false, true)}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', px: '16px', pb: '12px', pt: '2px' }}>
                    <Button
                        size="small"
                        onClick={() => {
                            if (!activeChartKey) return;
                            setDraftDateRange({ startDate: '', endDate: '' });
                        }}
                        sx={{ color: '#777', textTransform: 'capitalize' }}
                    >
                        Clear
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={handleApply}
                    >
                        Apply
                    </Button>
                    {/* <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                            if (!activeChartKey) return;

                            const params = dateRangeParams[activeChartKey];
                            switch (activeChartKey) {
                                case 'listeningTime':
                                    dispatch(fetchDashboardData({ listeningTime: params }));
                                    break;

                                case 'userGrowth':
                                    dispatch(fetchDashboardData({ userGrowth: params }));
                                    break;

                                case 'conversion':
                                    dispatch(fetchDashboardData({ conversion: params }));
                                    break;

                                default:
                                    break;
                            }

                            closeDateRangePicker();
                        }}
                    >
                        Apply
                    </Button> */}
                    {/* <Button
                        size="small"
                        variant="contained"
                        onClick={closeDateRangePicker}
                        onClick={() => {
                            dispatch(fetchDashboardData(dateRangeParams));
                            closeDateRangePicker();
                        }}
                        sx={{ backgroundColor: '#FF69B4', color: '#fff', textTransform: 'capitalize', px: '18px', boxShadow: '0 2px 6px rgba(255, 105, 180, 0.35)', '&:hover': { backgroundColor: '#f052a5' } }}
                    >
                        Apply
                    </Button> */}
                </Box>
            </Popover>
        </>
    );
}

export default Dashboard;
